import { ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { deleteApplicationAsset } from '@/api/Rover/ApplicationAssets';
import type { IGetApplicationAssetsServiceResData } from '@/api/Rover/ApplicationAssets/types';
import Confirm from '@/components/UI/Modal/Confirm';
import { RoverReactQueryKey } from '../../../../../../constants/ReactQueryKeys';
import documentIcon from '../../../../../../images/icons/document-icon.png';
import msExcelSheetDocIcon from '../../../../../../images/icons/excel-sheet-icon.png';
import mswordDocIcon from '../../../../../../images/icons/microsoft-word-document-icon.png';
import pdfDocIcon from '../../../../../../images/icons/pdf-doc-icon.png';
import msPowerPointDocIcon from '../../../../../../images/icons/powerpoint-document-icon.png';
import { notifyUser } from '../../../../../../store/Toast';

import { formatDate2, sanitizeResponseError } from '../../../../../../utils';

const fileTypeIcon: { [key: string]: { icon: any; fileTypes: string[] } } = {
  pdf: { icon: pdfDocIcon, fileTypes: ['.pdf', 'application/pdf'] },
  msdoc: {
    icon: mswordDocIcon,
    fileTypes: [
      '.doc',
      '.docx',
      '.dotx',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
      'application/msword',
    ],
  },
  mspp: {
    icon: msPowerPointDocIcon,
    fileTypes: [
      '.pptx',
      '.sldx',
      '.ppsx',
      '.potx',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.openxmlformats-officedocument.presentationml.slide',
      'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
      'application/vnd.openxmlformats-officedocument.presentationml.template',
    ],
  },
  msexcel: {
    icon: msExcelSheetDocIcon,
    fileTypes: [
      '.xlsx',
      '.xltx',
      '.csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
      'text/csv',
    ],
  },
  default: {
    icon: documentIcon,
    fileTypes: [],
  },
};

const fileTypeIconFinder = (contentType: string) => {
  let imgIcon = documentIcon;
  Object.keys(fileTypeIcon).forEach((key) => {
    if (fileTypeIcon[key].fileTypes.includes(contentType)) {
      imgIcon = fileTypeIcon[key].icon;
    }
  });
  return imgIcon;
};

const DueDiligenceDocumentItem = ({ file }: { file: IGetApplicationAssetsServiceResData }) => {
  const { applicationId } = useParams<string>();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const removeApplicationAsset = useMutation(
    (payload: { assetId: number; applicationId: string }) =>
      deleteApplicationAsset({
        assetId: payload.assetId,
        applicationId: payload.applicationId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATION_ASSETS]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${file?.name} deleted successfully.`,
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const deleteLibraryItemHandler = () => {
    setShowDeleteConfirm(true);
  };

  const deleteLibraryItemConfirmHandler = () => {
    const data = {
      assetId: Number(file.id),
      applicationId: applicationId!,
    };
    removeApplicationAsset.mutate(data);
    setShowDeleteConfirm(false);
  };

  const deleteLibraryItemCancelHandler = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <div className='w-full bg-white flex justify-between my-2 p-4 shadow-md rounded-md border'>
      {showDeleteConfirm && (
        <Confirm
          title='Delete File'
          message={`You are about to delete ${file.name} file. Are you sure you want to delete ?`}
          acceptButtonLabel='Delete'
          declineButtonLabel='Cancel'
          accept={deleteLibraryItemConfirmHandler}
          decline={deleteLibraryItemCancelHandler}
        />
      )}
      <div className='flex gap-8 items-center'>
        <div>
          <img src={fileTypeIconFinder(file.contentType)} className='h-auto w-10 object-fit' alt='file-icon' />
        </div>
        <div className='flex flex-col'>
          <p className='text-sm text-gray-600 mb-2 font-semibold'>{file.name}</p>
          <div className='text-xs'>
            Submitted Date: {formatDate2(file.createdAt!)} <span className='inline-block w-2 h-2 mx-2 bg-green-400 rounded-full'></span>{' '}
            Submitted By: Sage Admin
          </div>
        </div>
      </div>
      <div className='flex gap-2 items-center'>
        <div className='bg-gray-100 p-2 rounded-full hover:bg-gray-200 flex items-center justify-center cursor-pointer'>
          <a href={file?.key} target='_blank' rel='noreferrer noopener'>
            <ArrowDownTrayIcon className='h-5 w-5 text-green-500' />
          </a>
        </div>
        <div
          className='bg-gray-100 p-2 rounded-full hover:bg-gray-200 flex items-center justify-center cursor-pointer'
          onClick={deleteLibraryItemHandler}
        >
          <TrashIcon className='h-5 w-5 text-tertiaryColor-normal' />
        </div>
      </div>
    </div>
  );
};

export default DueDiligenceDocumentItem;
